/**
 * 채팅 입력창 초안(draft) 저장소 — 채널별로 작성 중인 내용을 보관했다가 그 방에 돌아오면 되살린다.
 *
 * ChatWindow는 조건부 마운트라(App.tsx의 `isMobile ? isMobileChatOpen : isDesktopChatOpen`)
 * 데스크톱 X 닫기·모바일 뒤로가기·홈 탭 전환에서 통째로 언마운트된다 — state에만 두면 복원 경로가
 * 아예 없다. 그래서 컴포넌트 바깥의 모듈 스코프에 둔다.
 *
 * 2층 구조:
 *  - 메모리(모듈 스코프 Map): File·Message 객체까지 그대로. 화면 전환·채널 전환 왕복용
 *  - localStorage: 직렬화 가능한 것만(본문 + 답장 축약본 + 멘션 원장). 새로고침·앱 재시작용
 *
 * ⚠ 첨부는 localStorage에 절대 넣지 않는다. ChatWindow.addFiles가 이미지 미리보기를 base64
 *   data URL로 만들어 url·thumbnail 양쪽에 박기 때문에 몇 장만으로 5MB 쿼터를 채우고, 그 순간
 *   activeChannel·currentUser 저장까지 함께 실패해 앱 상태 복원이 통째로 깨진다.
 * ⚠ 이 키는 기기 설정이 아니라 세션 콘텐츠다 — webNotifyEnabled·disclaimer와 달리
 *   App.tsx handleLogout에서 반드시 지운다(clearAllDrafts).
 */
import { Attachment, Channel, MentionInput, Message, isDraftChannel } from '../types';

const LS_PREFIX = 'chatDraft:'; // chatDraft:{userId} — disclaimer:{userId}와 같은 계정별 키
const SCHEMA_VERSION = 1;
/** 메모리 항목 수. 첨부 base64 썸네일이 여기 살기 때문에 이 값이 곧 메모리 상한이다 */
const MEM_LIMIT = 10;
const STORE_LIMIT = 30;
const TTL_MS = 14 * 24 * 60 * 60 * 1000;
/** 초과분은 메모리에만 둔다 — 사용자가 쓴 글을 조용히 잘라 저장하지 않는다 */
const TEXT_MAX = 20000;
/** 답장 배너는 truncate 한 줄이라(ChatWindow의 답장 배너) 그 이상은 화면에 나오지 않는다 */
const REPLY_TEXT_MAX = 200;

export interface DraftSnapshot {
  text: string;
  replyTo: Message | null;
  /** ChatWindow의 pendingMentionsRef와 같은 구조 (표시명 → 전송용 구조) */
  mentions: Map<string, MentionInput>;
  attachments: Attachment[];
  pendingFiles: Map<string, File>;
}

export const emptyDraft = (): DraftSnapshot => ({
  text: '',
  replyTo: null,
  mentions: new Map(),
  attachments: [],
  pendingFiles: new Map(),
});

export const isDraftEmpty = (d: DraftSnapshot): boolean =>
  !d.text.trim() && d.attachments.length === 0 && !d.replyTo;

/**
 * 초안 DM은 상대가 누구든 id가 0(DRAFT_CHANNEL_ID)이라 채널 id만으로는 키가 될 수 없다.
 * 접두사로 두 공간을 나눈다 — 실제 채널 id는 BIGSERIAL(1부터)이라 절대 겹치지 않는다.
 *
 * 여기 저장되는 것은 채널 객체가 아니라 상대 사번 문자열 하나다. 이 키로 유령 방을 되살릴 경로는
 * 없고(목록·부팅 복원은 이 모듈을 읽지 않는다) 이미 열려 있는 입력창의 복원에만 쓰인다.
 */
export function draftKeyOf(channel: Channel): string {
  return isDraftChannel(channel) ? `dm:${channel.draftPartner?.id ?? ''}` : `ch:${channel.id}`;
}

// ----------------------------------------
// 메모리 층
// ----------------------------------------

// 키에 userId를 넣는 이유: clearAllDrafts 호출이 누락되는 경로(앱 강제 종료 후 다른 계정 로그인)
// 에서도 남의 초안을 읽지 못하게 하는 2차 방어다.
const memory = new Map<string, DraftSnapshot>();
const memKey = (userId: string, key: string) => `${userId}|${key}`;

/**
 * 호출부가 넘긴 스냅샷은 ChatWindow의 렌더 미러 객체라 이후 렌더에서 필드가 계속 바뀐다.
 * 그대로 담으면 방을 옮긴 뒤의 입력이 이전 방 초안을 덮어쓰므로 반드시 복사해서 넣는다.
 */
function putMemory(userId: string, key: string, d: DraftSnapshot): void {
  const k = memKey(userId, key);
  memory.delete(k);
  memory.set(k, {
    text: d.text,
    replyTo: d.replyTo,
    mentions: new Map(d.mentions),
    attachments: d.attachments.slice(),
    pendingFiles: new Map(d.pendingFiles),
  });
  if (memory.size > MEM_LIMIT) {
    const oldest = memory.keys().next().value; // Map은 삽입 순서를 보장한다
    if (oldest !== undefined) memory.delete(oldest);
  }
}

// ----------------------------------------
// localStorage 층
// ----------------------------------------

interface StoredReply {
  id: number;
  channelId: number;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: string;
  deleted?: boolean;
}

interface StoredDraft {
  text: string;
  replyTo?: StoredReply;
  mentions?: [string, MentionInput][];
  at: number;
}

interface DraftBlob {
  v: number;
  items: Record<string, StoredDraft>;
}

const emptyBlob = (): DraftBlob => ({ v: SCHEMA_VERSION, items: {} });

/** 파싱 실패·스키마 불일치·버전 불일치는 전부 "초안 없음"으로 접는다(disclaimer.ts와 같은 폴백) */
function readBlob(userId: string): DraftBlob {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LS_PREFIX + userId);
  } catch {
    return emptyBlob();
  }
  if (!raw) return emptyBlob();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== SCHEMA_VERSION || typeof parsed.items !== 'object') return emptyBlob();
    const items: Record<string, StoredDraft> = {};
    Object.keys(parsed.items).forEach(k => {
      const it = parsed.items[k];
      if (!it || typeof it.text !== 'string' || typeof it.at !== 'number') return;
      items[k] = { text: it.text, at: it.at, replyTo: parseReply(it.replyTo), mentions: parseMentions(it.mentions) };
    });
    return { v: SCHEMA_VERSION, items };
  } catch {
    return emptyBlob();
  }
}

function parseReply(r: any): StoredReply | undefined {
  if (!r || typeof r.id !== 'number' || typeof r.senderName !== 'string') return undefined;
  return {
    id: r.id,
    channelId: typeof r.channelId === 'number' ? r.channelId : 0,
    senderId: String(r.senderId ?? ''),
    senderName: r.senderName,
    text: typeof r.text === 'string' ? r.text : '',
    timestamp: typeof r.timestamp === 'string' ? r.timestamp : '',
    deleted: !!r.deleted,
  };
}

function parseMentions(m: any): [string, MentionInput][] | undefined {
  if (!Array.isArray(m)) return undefined;
  const out: [string, MentionInput][] = [];
  m.forEach(entry => {
    if (!Array.isArray(entry) || typeof entry[0] !== 'string') return;
    const v = entry[1];
    if (!v || (v.type !== 'user' && v.type !== 'dept' && v.type !== 'all')) return;
    out.push([entry[0], v as MentionInput]);
  });
  return out.length > 0 ? out : undefined;
}

/** TTL 만료분과 상한 초과분(오래된 것부터)을 떨군다 */
function prune(blob: DraftBlob): void {
  const now = Date.now();
  const keys = Object.keys(blob.items);
  keys.forEach(k => {
    if (now - blob.items[k].at > TTL_MS) delete blob.items[k];
  });
  const left = Object.keys(blob.items);
  if (left.length > STORE_LIMIT) {
    left
      .sort((a, b) => blob.items[a].at - blob.items[b].at)
      .slice(0, left.length - STORE_LIMIT)
      .forEach(k => delete blob.items[k]);
  }
}

function keepNewest(blob: DraftBlob, n: number): void {
  const keys = Object.keys(blob.items);
  if (keys.length <= n) return;
  keys
    .sort((a, b) => blob.items[a].at - blob.items[b].at)
    .slice(0, keys.length - n)
    .forEach(k => delete blob.items[k]);
}

/**
 * 항상 read-modify-write — 블롭을 통째로 덮어쓰면 다른 탭이 쓴 다른 채널 초안을 날린다.
 * item이 null이면 그 키를 지운다.
 */
function writeItem(userId: string, key: string, item: StoredDraft | null): void {
  const blob = readBlob(userId);
  // 지울 것이 없으면 아무것도 하지 않는다 — 초안 없는 방을 열 때마다 쓰기가 돌지 않게
  if (!item && !blob.items[key]) return;
  if (item) blob.items[key] = item;
  else delete blob.items[key];
  prune(blob);

  const lsKey = LS_PREFIX + userId;
  try {
    if (Object.keys(blob.items).length === 0) localStorage.removeItem(lsKey);
    else localStorage.setItem(lsKey, JSON.stringify(blob));
  } catch {
    // 쿼터 초과 — 최신 것만 남기고 한 번 더 시도한다. 그래도 실패하면 조용히 포기한다
    // (다음 부팅에 그 방의 초안이 없을 뿐, 다른 키의 저장까지 말려들게 하지 않는다)
    try {
      keepNewest(blob, 5);
      if (Object.keys(blob.items).length === 0) localStorage.removeItem(lsKey);
      else localStorage.setItem(lsKey, JSON.stringify(blob));
    } catch {
      /* 포기 */
    }
  }
}

function serialize(d: DraftSnapshot): StoredDraft | null {
  // 본문이 상한을 넘으면 localStorage에는 남기지 않는다(메모리 층에는 그대로 있다).
  // 잘라 저장하면 돌아왔을 때 뒷부분이 사라진 글을 보게 된다.
  const text = d.text.length > TEXT_MAX ? '' : d.text;
  const mentions = Array.from(d.mentions.entries());
  const item: StoredDraft = { text, at: Date.now() };
  if (d.replyTo) {
    item.replyTo = {
      id: d.replyTo.id,
      channelId: d.replyTo.channelId,
      senderId: String(d.replyTo.senderId),
      senderName: d.replyTo.senderName,
      text: (d.replyTo.text || '').slice(0, REPLY_TEXT_MAX),
      timestamp: d.replyTo.timestamp,
      deleted: !!d.replyTo.deletedAt,
    };
  }
  if (mentions.length > 0) item.mentions = mentions;
  // 텍스트가 상한 초과로 비워졌고 답장도 없으면 남길 게 없다
  if (!item.text && !item.replyTo) return null;
  return item;
}

/**
 * 본문에 이름이 남은 멘션만 살린다 — ChatWindow.collectMentions의 재스캔 규칙과 같다.
 * 이게 없으면 복원된 본문에서 지운 멘션이 원장에만 남아 엉뚱한 사람에게 알림이 간다.
 */
function mentionsSurvivingText(entries: [string, MentionInput][], text: string): Map<string, MentionInput> {
  const out = new Map<string, MentionInput>();
  entries.forEach(([name, m]) => {
    const alive = m.type === 'all' ? text.includes('@all') : text.includes('@' + name);
    if (alive) out.set(name, m);
  });
  return out;
}

// ----------------------------------------
// 공개 API
// ----------------------------------------

/** 메모리 층 우선(첨부·원본 Message가 살아 있다). 없으면 localStorage에서 되살린다 */
export function readDraft(userId: string, key: string): DraftSnapshot | null {
  const mem = memory.get(memKey(userId, key));
  if (mem) return mem;

  const stored = readBlob(userId).items[key];
  if (!stored) return null;
  if (Date.now() - stored.at > TTL_MS) return null;

  const r = stored.replyTo;
  const replyTo: Message | null = r
    ? {
        id: r.id,
        channelId: r.channelId,
        text: r.text,
        senderId: r.senderId,
        senderName: r.senderName,
        timestamp: r.timestamp,
        // 배너는 deletedAt의 유무만 본다 — 삭제 시각 자체는 화면에 나오지 않는다
        deletedAt: r.deleted ? r.timestamp : null,
      }
    : null;

  return {
    text: stored.text,
    replyTo,
    mentions: mentionsSurvivingText(stored.mentions ?? [], stored.text),
    attachments: [], // File은 직렬화가 불가능하다 — 앱 재시작으로 사라지는 것이 명세된 한계
    pendingFiles: new Map(),
  };
}

/** 비어 있으면 저장이 아니라 삭제다 — 사용자가 다 지우고 나가면 초안도 사라져야 한다 */
export function saveDraft(userId: string, key: string, d: DraftSnapshot): void {
  if (!userId || !key) return;
  if (isDraftEmpty(d)) {
    clearDraft(userId, key);
    return;
  }
  putMemory(userId, key, d);
  writeItem(userId, key, serialize(d));
}

export function clearDraft(userId: string, key: string): void {
  if (!userId || !key) return;
  memory.delete(memKey(userId, key));
  writeItem(userId, key, null);
}

/**
 * 로그아웃·만료 토큰 정리용. handleLogout이 deps 빈 useCallback이라 userId를 읽을 수 없으므로
 * 인자를 받지 않고 chatDraft: 접두사 키를 전부 지운다.
 */
/** 테스트 전용 — 메모리 층만 비워 앱 재시작(localStorage만 남은 상태)을 흉내낸다 */
export function __dropMemoryForTest(): void {
  memory.clear();
}

export function clearAllDrafts(): void {
  memory.clear();
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) doomed.push(k);
    }
    doomed.forEach(k => localStorage.removeItem(k));
  } catch {
    /* 접근 불가 환경 — 메모리만 비우고 넘어간다 */
  }
}
